/**
 * Extracts a frame from a video element at a specific timestamp
 * @param video - The video element to extract from
 * @param timestamp - The timestamp in seconds to extract the frame at
 * @param maxDimension - Maximum size for the longest dimension (default: 512px)
 * @param quality - JPEG quality from 0 to 1 (default: 0.6)
 * @returns Promise that resolves to a data URL of the extracted frame
 */
export function extractFrameAtTime(
  video: HTMLVideoElement,
  timestamp: number,
  maxDimension: number = 512,
  quality: number = 0.6,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }

    const seekHandler = () => {
      try {
        // Calculate scaled dimensions (max on longest side)
        const originalWidth = video.videoWidth;
        const originalHeight = video.videoHeight;

        let scaledWidth = originalWidth;
        let scaledHeight = originalHeight;

        // Scale down if either dimension exceeds maxDimension
        if (originalWidth > maxDimension || originalHeight > maxDimension) {
          const aspectRatio = originalWidth / originalHeight;

          if (originalWidth > originalHeight) {
            // Landscape: limit width
            scaledWidth = maxDimension;
            scaledHeight = Math.round(maxDimension / aspectRatio);
          } else {
            // Portrait: limit height
            scaledHeight = maxDimension;
            scaledWidth = Math.round(maxDimension * aspectRatio);
          }
        }

        // Set canvas to scaled dimensions
        canvas.width = scaledWidth;
        canvas.height = scaledHeight;

        // Draw scaled image
        ctx.drawImage(video, 0, 0, scaledWidth, scaledHeight);

        // Convert to data URL
        const dataURL = canvas.toDataURL("image/jpeg", quality);
        resolve(dataURL);
      } catch (error) {
        reject(error);
      } finally {
        // Clean up
        video.removeEventListener("seeked", seekHandler);
        // Clear canvas to free memory
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
      }
    };

    const errorHandler = () => {
      video.removeEventListener("seeked", seekHandler);
      video.removeEventListener("error", errorHandler);
      reject(new Error("Video seek failed"));
    };

    video.addEventListener("seeked", seekHandler);
    video.addEventListener("error", errorHandler);
    video.currentTime = timestamp;
  });
}

/**
 * Gets the duration of a media file (video or audio)
 * @param file - The media file to get duration from
 * @returns Promise that resolves to the duration in seconds
 */
export function getMediaDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const isAudio = file.type.startsWith("audio/");
    const media = isAudio ? document.createElement("audio") : document.createElement("video");
    let objectUrl: string | null = null;

    const cleanup = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      media.removeEventListener("loadedmetadata", loadedHandler);
      media.removeEventListener("error", errorHandler);
      media.src = "";
    };

    const loadedHandler = () => {
      try {
        const duration = media.duration;
        cleanup();
        if (isNaN(duration) || duration === Infinity) {
          reject(new Error("Could not determine media duration"));
        } else {
          resolve(duration);
        }
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    const errorHandler = () => {
      cleanup();
      reject(new Error("Failed to load media"));
    };

    media.preload = "metadata";
    media.muted = true;
    if (!isAudio) {
      (media as HTMLVideoElement).playsInline = true;
    }

    media.addEventListener("loadedmetadata", loadedHandler);
    media.addEventListener("error", errorHandler);

    objectUrl = URL.createObjectURL(file);
    media.src = objectUrl;
  });
}

/**
 * Creates a thumbnail from a video file
 * @param file - The video file to create thumbnail from
 * @param timestamp - The timestamp in seconds to extract (default: 1 second or video start)
 * @param maxDimension - Maximum size for the longest dimension (default: 512px)
 * @param quality - JPEG quality from 0 to 1 (default: 0.6)
 * @returns Promise that resolves to a data URL of the thumbnail
 */
export function createVideoThumbnail(
  file: File,
  timestamp?: number,
  maxDimension: number = 512,
  quality: number = 0.6,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    let objectUrl: string | null = null;

    const cleanup = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      video.removeEventListener("loadedmetadata", loadedHandler);
      video.removeEventListener("error", errorHandler);
      video.src = "";
    };

    const loadedHandler = async () => {
      try {
        // Use provided timestamp, or default to 1 second (or start of video if shorter)
        const seekTime = timestamp !== undefined ? timestamp : Math.min(1, video.duration);
        const dataURL = await extractFrameAtTime(video, seekTime, maxDimension, quality);
        cleanup();
        resolve(dataURL);
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    const errorHandler = () => {
      cleanup();
      reject(new Error("Failed to load video"));
    };

    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    video.addEventListener("loadedmetadata", loadedHandler);
    video.addEventListener("error", errorHandler);

    objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;
  });
}
